import React, { useEffect, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Spin, Modal, Form, Input, App as AntdApp } from 'antd';
import { UserOutlined, LogoutOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import Dashboard from './index';
import UserList from '../user/user_list';
import UserDeviceList from '../user/user_device_list';
import GroupList from '../system/group_list';
import UserAssetList from '../asset/user_asset_list';
import NodeList from '../data/node_list';
import DepositList from '../chain/deposit_list';
import WithdrawList from '../chain/withdraw_list';
import './style.css';

const { Header, Sider, Content } = Layout;

const menuItems = [
  { key: 'dashboard', label: '运营总览', path: '/' },
  {
    key: 'user',
    label: '用户管理',
    children: [
      { key: 'user-list', label: '用户列表', path: '/user-list' },
      { key: 'user-device', label: '用户设备', path: '/user-device' },
    ],
  },
  {
    key: 'asset',
    label: '资产管理',
    children: [
      { key: 'user-asset', label: '用户资产', path: '/user-asset' },
      { key: 'asset-log', label: '资产流水', path: '/asset-log' },
    ],
  },
  {
    key: 'data',
    label: '数据管理',
    children: [
      { key: 'device-release', label: '设备产出', path: '/device-release' },
      { key: 'node-list', label: '节点列表', path: '/node-list' },
      { key: 'user_withdraw', label: '提取记录', path: '/user_withdraw' },
    ],
  },
  {
    key: 'chain',
    label: '链上记录',
    children: [
      { key: 'deposit-list', label: '充记录', path: '/deposit-list' },
      { key: 'withdraw-list', label: '提记录', path: '/withdraw-list' },
    ],
  },
  {
    key: 'system',
    label: '系统管理',
    children: [
      { key: 'group-list', label: '分组管理', path: '/group-list' },
    ],
  },
];

const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>(getOpenMenuKeys(location.pathname));
  const [modal, contextHolder] = Modal.useModal();

  // 修改密码弹窗表单
  const [form] = Form.useForm();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      setUserEmail(session.user.email || '');
      setLoading(false);
    };
    checkSession();
  }, [navigate]);

  useEffect(() => {
    setOpenKeys(getOpenMenuKeys(location.pathname));
  }, [location.pathname]);

  // 菜单展开/收起事件
  const handleOpenChange = (keys: string[]) => {
    // 允许多开，直接设置，不做任何限制
    setOpenKeys(keys);
  };

  const handleMenuClick = ({ key, keyPath }: any) => {
    const item = findMenuItemByKey(menuItems, key);
    if (item && item.path) {
      navigate(item.path);
      // 不再自动收起其它菜单，保持 openKeys 不变
    }
  };

  const findMenuItemByKey = (items: any[], key: string): any => {
    for (const item of items) {
      if (item.key === key) return item;
      if (item.children) {
        const found = findMenuItemByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleChangePwd = () => {
    setShowPwdModal(true);
  };

  const handlePwdOk = async () => {
    try {
      setPwdLoading(true);
      const values = await form.validateFields();
      const { password, newPassword } = values;
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) throw new Error('请重新登录');
      const email = sessionData.session.user.email || '';
      const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError) {
        let msg = loginError.message || '原密码错误';
        if (loginError.code === 'invalid_credentials') {
          msg = '原密码错误';
        }
        modal.error({ title: '修改失败', content: msg, centered: true });
        setPwdLoading(false);
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        const msg = updateError.message || '修改失败，请重试';
        modal.error({ title: '修改失败', content: msg, centered: true });
        setPwdLoading(false);
        return;
      }
      setShowPwdModal(false);
      form.resetFields();
      modal.success({ title: '修改成功', content: '密码已更新，请使用新密码重新登录。', centered: true, onOk: handleLogout });
    } catch (e: any) {
      if (e && e.errorFields && Array.isArray(e.errorFields)) {
        modal.error({ title: '表单校验失败', content: e.errorFields.map((f:any) => f.errors.join('，')).join('；'), centered: true });
      } else {
        modal.error({ title: '修改失败', content: e.message || '发生未知错误', centered: true });
      }
    } finally {
      setPwdLoading(false);
    }
  };

  // userMenu 改为 items 写法
  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: handleChangePwd,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 根据当前路径递归查找菜单key
  function getSelectedMenuKey(pathname: string) {
    function findKey(items: any[]): string | undefined {
      for (const item of items) {
        if (item.path === pathname) return item.key;
        if (item.children) {
          const found = findKey(item.children);
          if (found) return found;
        }
      }
      return undefined;
    }
    return findKey(menuItems) || 'dashboard';
  }

  // 递归查找当前key的所有父级key
  function getOpenMenuKeys(pathname: string) {
    // 默认全部一级菜单都展开
    return menuItems.filter(item => item.children).map(item => item.key);
  }

  if (loading) {
    return <div className="dashboard-loading"><Spin size="large" /></div>;
  }

  return (
    <AntdApp>
      {contextHolder}
      <Layout style={{ minHeight: '100vh' }}>
        <Sider /* collapsible collapsed={collapsed} onCollapse={setCollapsed} */ width={220} style={{ background: '#fff8ef' }}>
          <div className="dashboard-logo" style={{ display: 'flex', alignItems: 'center', padding: 24, justifyContent: 'center' }}>
            <img src="/icon.png" alt="logo" style={{ width: 32, height: 32, marginRight: 8 }} />
            <span style={{ fontWeight: 700, fontSize: 16, color: '#4b2e13', letterSpacing: 1, whiteSpace: 'nowrap', lineHeight: '32px' }}>UCDN 管理后台</span>
          </div>
          <Menu
            mode="inline"
            selectedKeys={[getSelectedMenuKey(location.pathname)]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            style={{ borderRight: 0, background: 'transparent', fontWeight: 600 }}
            items={menuItems}
            onClick={handleMenuClick}
            // remove force close logic, keep openKeys as user toggles
          />
        </Sider>
        <Layout>
          <Header style={{ background: '#fff8ef', padding: '0 24px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: 64 }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={["click"]}>
              <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <Avatar style={{ background: '#ffb86b', color: '#4b2e13', marginRight: 8 }} icon={<UserOutlined />} />
                <span style={{ color: '#4b2e13', fontWeight: 700 }}>{userEmail}</span>
              </span>
            </Dropdown>
          </Header>
          <Content style={{ margin: 0, padding: 32, background: '#fff8ef', minHeight: 360 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/user-list" element={<UserList />} />
              <Route path="/user-device" element={<UserDeviceList />} />
              <Route path="/group-list" element={<GroupList />} />
              <Route path="/user-asset" element={<UserAssetList />} />
              <Route path="/asset-log" element={<React.Suspense fallback={null}>{React.createElement(require('../asset/asset_log_list').default)}</React.Suspense>} />
              <Route path="/device-release" element={<React.Suspense fallback={null}>{React.createElement(require('../data/device_release_list').default)}</React.Suspense>} />
              <Route path="/node-list" element={<NodeList />} />
              <Route path="/deposit-list" element={<DepositList />} />
              <Route path="/withdraw-list" element={<WithdrawList />} />
              <Route path="/user_withdraw" element={<React.Suspense fallback={null}>{React.createElement(require('../data/user_withdraw_list').default)}</React.Suspense>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <Modal
              title="修改密码"
              open={showPwdModal}
              onOk={handlePwdOk}
              onCancel={() => setShowPwdModal(false)}
              confirmLoading={pwdLoading}
              okText="确定"
              cancelText="取消"
              maskClosable={false}
            >
              <Form form={form} layout="vertical">
                <Form.Item name="password" label="原密码" rules={[{ required: true, message: '请输入原密码' }]}>
                  <Input.Password placeholder="请输入原密码" autoComplete="current-password" />
                </Form.Item>
                <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少6位' }]}>
                  <Input.Password placeholder="请输入新密码" autoComplete="new-password" />
                </Form.Item>
              </Form>
            </Modal>
          </Content>
        </Layout>
      </Layout>
    </AntdApp>
  );
};

export default DashboardLayout;
